import { Avatar, Box, Grid, InputAdornment, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { differenceInMinutes, format } from 'date-fns';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import uniqid from 'uniqid';
import { Shift } from '../../apiTypes';
import EditModal from '../../components/EditModal';
import FormPriceField from '../../components/forms/FormPriceField';
import FormSelect from '../../components/forms/FormSelect';
import FormTextField from '../../components/forms/FormTextField';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { useSnackbar } from '../../contexts/snackbar.context';
import { db } from '../../firebase';
import { deleteDoc, setDoc, updateDoc } from '../../utils/api';
import { getDayTimeRange } from '../../utils/dates';

type Props = {
  open: boolean;
  shift?: DbDocument<Shift> | null;
  initialWorkerId?: string | null;
  initialDate?: Date | null;
  fastCreate?: boolean;
  onClose: () => void;
};

type FormType = Omit<Shift, 'workerName'>;

const getInitStart = (initialDate?: Date | null) => {
  const date = initialDate ? new Date(initialDate) : new Date();
  date.setHours(8);
  date.setMinutes(0);
  date.setSeconds(0);
  date.setMilliseconds(0);
  return date.getTime();
};

const getInitEnd = (initialDate?: Date | null) => {
  const date = initialDate ? new Date(initialDate) : new Date();
  date.setHours(16);
  date.setMinutes(0);
  date.setSeconds(0);
  date.setMilliseconds(0);
  return date.getTime();
};

const EditShift = ({ shift, open, initialWorkerId, initialDate, fastCreate, onClose }: Props) => {
  const { cafeSlug, cafe } = useAdmin();
  const { currency } = cafe || {};
  const { lang } = useLocale();
  const { addSnackbar } = useSnackbar();
  const { workersMap, workers: allWorkers, worker } = useCafe();

  const workers = useMemo(() => {
    const { edit_work_shifts_of_other_employees, edit_own_work_shifts, allow_create_shift_on_login } =
      worker?.credentials || {};

    if (edit_work_shifts_of_other_employees && edit_own_work_shifts) {
      return allWorkers;
    }
    if (edit_work_shifts_of_other_employees && !edit_own_work_shifts) {
      return allWorkers.filter((w) => w.id !== worker?.id);
    }
    if (worker && !edit_work_shifts_of_other_employees && edit_own_work_shifts) {
      return [worker];
    }
    if (worker && allow_create_shift_on_login) {
      return [worker];
    }
    return [];
  }, [allWorkers, worker]);

  const workerIds = useMemo(() => workers.map((w) => w.id), [workers]);

  const initialWorker =
    initialWorkerId && workerIds.includes(initialWorkerId) ? workersMap[initialWorkerId] : undefined;

  const disabled = Boolean(shift?.paid);

  const form = useForm<FormType>({
    mode: 'onChange',
    defaultValues: {
      start: shift?.start || getInitStart(initialDate),
      end: shift?.end || getInitEnd(initialDate),
      workerId: shift?.workerId || initialWorker?.id || '',
      ratePerHour: shift?.ratePerHour || initialWorker?.ratePerHour || 0,
      percentageOfSales: shift?.percentageOfSales || initialWorker?.percentageOfSales || 0,
      comment: shift?.comment || '',
      paid: shift?.paid || false,
      bonus: shift?.bonus || 0,
    },
    shouldUnregister: false,
  });

  const { setValue } = form;
  const start = form.watch('start');
  const end = form.watch('end');
  const workerId = form.watch('workerId');

  const diff = useMemo(() => differenceInMinutes(end, start), [end, start]);

  const diffString = useMemo(() => {
    const diffHoursString = Math.floor(diff / 60) > 9 ? `${Math.floor(diff / 60)}` : `0${Math.floor(diff / 60)}`;
    const diffMinutesString = Math.floor(diff % 60) > 9 ? `${Math.floor(diff % 60)}` : `0${Math.floor(diff % 60)}`;
    return `${diffHoursString}:${diffMinutesString}`;
  }, [diff]);

  const handleClose = useCallback(() => {
    onClose();
    form.reset();
  }, [form, onClose]);

  const saveChanges = useCallback(
    async (formData: FormType) => {
      if (formData.start > 0) {
        const newShift: Shift = {
          ...formData,
          workerName: workersMap[formData.workerId]?.name || '',
          percentageOfSales: Number(formData.percentageOfSales),
          ratePerHour: Number(formData.ratePerHour),
          bonus: Number(formData.bonus),
        };

        const [dayStart, dayEnd] = getDayTimeRange(formData.start);

        const q = query(
          collection(db, 'cafes', cafeSlug, 'shifts'),
          where('workerId', '==', formData.workerId),
          where('start', '>=', dayStart),
          where('start', '<=', dayEnd)
        );

        const querySnapshot = await getDocs(q);
        let isDublicate = false;

        querySnapshot.forEach((doc) => {
          const { start, end } = doc.data() as Shift;
          if (start < formData.end && end > formData.start && doc.id !== shift?.id) {
            isDublicate = true;
            addSnackbar(t.save_shift_error_message[lang], 'error');
          }
        });

        if (!isDublicate) {
          if (shift?.id) {
            updateDoc<Shift>(`cafes/${cafeSlug}/shifts/${shift.id}`, newShift);
          } else {
            setDoc<Shift>(`cafes/${cafeSlug}/shifts/${uniqid()}`, newShift);
          }
        }

        handleClose();
      }
    },
    [addSnackbar, cafeSlug, handleClose, lang, shift?.id, workersMap]
  );

  const remove = useCallback(async () => {
    if (shift?.id) {
      deleteDoc(`cafes/${cafeSlug}/shifts/${shift.id}`);
      handleClose();
    }
  }, [cafeSlug, handleClose, shift?.id]);

  useEffect(() => {
    if (!shift?.id && workerId && workersMap[workerId]) {
      setValue('ratePerHour', workersMap[workerId]?.ratePerHour || 0);
      setValue('percentageOfSales', workersMap[workerId]?.percentageOfSales || 0);
    }
  }, [setValue, shift?.id, workerId, workersMap]);

  return (
    <EditModal<FormType>
      title={shift?.id ? t.shift[lang] : t.add_shift[lang]}
      open={open}
      onClose={handleClose}
      submitButton={shift ? t.save_changes[lang] : t.add[lang]}
      onSubmit={saveChanges}
      secondaryButton={shift ? t.remove[lang] : undefined}
      onSecondaryButtonClick={remove}
      confirmText={t.remove_shift_confirm[lang]}
      form={form}
      size="xs"
    >
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12}>
          <FormSelect
            name="workerId"
            required
            size="small"
            label={t.worker[lang]}
            disabled={fastCreate || disabled}
            options={workerIds}
            renderOption={(o) => {
              const worker = workersMap[o];
              return (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar src={worker?.photoUrl} />
                  <Typography ml={1}>{worker?.name || shift?.workerName}</Typography>
                </Box>
              );
            }}
            rules={{ required: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <DatePicker
            disableMaskedInput
            label={t.date[lang]}
            value={new Date(start)}
            disabled={fastCreate || disabled}
            onChange={(date) => {
              if (date && date.toString() !== 'Invalid Date') {
                const endDate = new Date(date);
                endDate.setMinutes(endDate.getMinutes() + diff);
                setValue('start', date.getTime());
                setValue('end', endDate.getTime());
              }
            }}
            renderInput={(params) => <TextField {...params} required color="info" fullWidth />}
            showToolbar={false}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t.start_time[lang]}
            type="time"
            required
            color="info"
            disabled={disabled}
            value={format(new Date(start), 'HH:mm')}
            onChange={(e) => {
              const date = new Date(start);
              date.setHours(Number(e.target.value.split(':')[0]));
              date.setMinutes(Number(e.target.value.split(':')[1]));
              const endDate = new Date(date);
              endDate.setMinutes(endDate.getMinutes() + diff);
              setValue('start', date.getTime());
              setValue('end', endDate.getTime());
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t.shift_duration[lang]}
            type="time"
            required
            color="info"
            value={diffString}
            disabled={disabled}
            onChange={(e) => {
              const endDate = new Date(start);
              endDate.setHours(endDate.getHours() + Number(e.target.value.split(':')[0]));
              endDate.setMinutes(endDate.getMinutes() + Number(e.target.value.split(':')[1]));
              setValue('end', endDate.getTime());
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label={t.end_time[lang]}
            type="time"
            required
            color="info"
            disabled={disabled}
            value={format(new Date(end), 'HH:mm')}
            onChange={(e) => {
              const endDate = new Date(start);
              endDate.setHours(Number(e.target.value.split(':')[0]));
              endDate.setMinutes(Number(e.target.value.split(':')[1]));
              if (endDate.getTime() < start) {
                endDate.setDate(endDate.getDate() + 1);
              }
              setValue('end', endDate.getTime());
            }}
          />
        </Grid>

        {!fastCreate && (
          <>
            <Grid item xs={12} sm={6}>
              <FormPriceField
                name="ratePerHour"
                label={t.rate_per_hour[lang]}
                fullWidth
                disabled={disabled}
                InputProps={{ startAdornment: <InputAdornment position="start">{currency}</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormPriceField
                name="bonus"
                label={t.award_fine[lang]}
                fullWidth
                canBeNegative
                disabled={disabled}
                InputProps={{ startAdornment: <InputAdornment position="start">{currency}</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormTextField
                name="percentageOfSales"
                type="number"
                label={t.percentage_of_sales[lang]}
                rules={{ validate: (v) => Number(v) >= 0 && Number(v) <= 100 }}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                fullWidth
                disabled={disabled}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12} sm={12}>
          <FormTextField name="comment" type="number" label={t.comment[lang]} fullWidth multiline rows={2} />
        </Grid>
      </Grid>
    </EditModal>
  );
};

export default EditShift;
